import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Sparkles, Search, Plus, CheckCircle2, Wand2, FileJson } from "lucide-react";
import { toast } from "sonner";
import { useIncrementalList } from "@/hooks/use-incremental-list";
import {
  listCurricula, listClasses, listSubjects, listTopics,
  searchQuestions, saveQuestion, setQuestionStatus, deleteQuestion,
  aiGenerateQuestions, bulkImportQuestions,
} from "@/lib/curriculum.functions";

export const Route = createFileRoute("/teacher/questions")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Question Bank · MAKAI" }] }),
  component: Page,
});

const QTYPES = [
  "multiple_choice","true_false","fill_blank","matching","short_answer","essay",
  "diagram_label","ordering","drag_drop","image","audio","video","practical",
] as const;

const DIFFS = ["easy", "medium", "hard", "expert"] as const;
const STATUSES = ["draft", "review", "approved", "published", "archived"] as const;

function Page() {
  const curricula = useQuery({ queryKey: ["cur"], queryFn: () => useServerFn(listCurricula)() });
  const [curId, setCurId] = useState("");
  const cur = curId || curricula.data?.[0]?.id || "";

  const classes = useQuery({
    queryKey: ["cls", cur], enabled: !!cur,
    queryFn: () => useServerFn(listClasses)({ data: { curriculumId: cur } }),
  });
  const subjects = useQuery({
    queryKey: ["sub", cur], enabled: !!cur,
    queryFn: () => useServerFn(listSubjects)({ data: { curriculumId: cur } }),
  });
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const topics = useQuery({
    queryKey: ["top", subjectId, classId],
    queryFn: () => useServerFn(listTopics)({ data: { subjectId, classId } }),
    enabled: !!subjectId && !!classId,
  });

  const [topicId, setTopicId] = useState("");
  const [difficulty, setDifficulty] = useState<string>("");
  const [qType, setQType] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [q, setQ] = useState("");

  const list = useQuery({
    queryKey: ["q-bank", { classId, subjectId, topicId, difficulty, qType, status, q }],
    queryFn: () =>
      useServerFn(searchQuestions)({
        data: {
          classId: classId || undefined,
          subjectId: subjectId || undefined,
          topicId: topicId || undefined,
          difficulty: (difficulty as never) || undefined,
          type: qType || undefined,
          status: (status as never) || undefined,
          q: q || undefined,
          limit: 100,
        },
      }),
  });

  const setStatusFn = useServerFn(setQuestionStatus);
  const delFn = useServerFn(deleteQuestion);
  const statusMut = useMutation({
    mutationFn: (v: { id: string; status: string }) => setStatusFn({ data: v as never }),
    onSuccess: () => { toast.success("Status updated"); list.refetch(); },
  });
  const delMut = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => { toast.success("Deleted"); list.refetch(); },
  });

  const bankRows = useMemo(() => list.data ?? [], [list.data]);
  const bank = useIncrementalList(bankRows, 25);

  const subjectName = useMemo(
    () => subjects.data?.find((s) => s.id === subjectId)?.name ?? "",
    [subjects.data, subjectId],
  );
  const className = useMemo(
    () => classes.data?.find((c) => c.id === classId)?.name ?? "",
    [classes.data, classId],
  );

  return (
    <AppShell role="teacher" title="Question Bank" subtitle="Curriculum-aware bank with AI generation">
      <div className="space-y-6 p-4 md:p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Filters</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 lg:grid-cols-6 gap-2">
            <Select value={cur} onValueChange={setCurId}>
              <SelectTrigger><SelectValue placeholder="Curriculum" /></SelectTrigger>
              <SelectContent>{curricula.data?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={classId} onValueChange={setClassId}>
              <SelectTrigger><SelectValue placeholder="Class" /></SelectTrigger>
              <SelectContent>{classes.data?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={subjectId} onValueChange={setSubjectId}>
              <SelectTrigger><SelectValue placeholder="Subject" /></SelectTrigger>
              <SelectContent>{subjects.data?.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={topicId} onValueChange={setTopicId}>
              <SelectTrigger><SelectValue placeholder="Topic" /></SelectTrigger>
              <SelectContent>{topics.data?.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger><SelectValue placeholder="Difficulty" /></SelectTrigger>
              <SelectContent>{DIFFS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <div className="col-span-2 lg:col-span-3 relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search question text…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Select value={qType} onValueChange={setQType}>
              <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>{QTYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-2 col-span-2">
              <AIGenerateDialog
                defaults={{ className, subject: subjectName, classId, subjectId, topicId }}
                onSaved={() => list.refetch()}
              />
              <BulkImportDialog
                defaults={{ classId, subjectId, topicId }}
                onDone={() => list.refetch()}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              Results {list.isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
            </CardTitle>
            <Badge variant="outline">{list.data?.length ?? 0} questions</Badge>
          </CardHeader>
          <CardContent>
            {!list.data?.length ? (
              <p className="text-sm text-muted-foreground py-10 text-center">No questions match these filters.</p>
            ) : (
              <div className="space-y-2">
                {bank.visible.map((it) => (
                  <div key={it.id} className="rounded-lg border p-3 hover:bg-muted/40 transition">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm line-clamp-2">{it.stem}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          <Badge variant="secondary">{it.type}</Badge>
                          <Badge variant="outline">{it.difficulty}</Badge>
                          {it.bloom_level && <Badge variant="outline">{it.bloom_level}</Badge>}
                          <Badge variant="outline">{it.marks ?? 1} mark{(it.marks ?? 1) === 1 ? "" : "s"}</Badge>
                          <Badge variant={it.status === "published" ? "default" : "outline"}>{it.status}</Badge>
                          {it.ai_generated && <Badge className="bg-primary/10 text-primary"><Sparkles className="h-3 w-3 mr-1" />AI</Badge>}
                          <span className="text-xs text-muted-foreground ml-2">v{it.version}</span>
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Select value="" onValueChange={(v) => statusMut.mutate({ id: it.id, status: v })}>
                          <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue placeholder="Move to…" /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" onClick={() => delMut.mutate(it.id)}>Delete</Button>
                      </div>
                    </div>
                  </div>
                ))}
                {bank.hasMore && (
                  <div ref={bank.sentinelRef} className="py-4 text-center text-xs text-muted-foreground">
                    Loading more questions…
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

/* ----------------- AI generation dialog ----------------- */

function AIGenerateDialog({
  defaults, onSaved,
}: {
  defaults: { className: string; subject: string; classId: string; subjectId: string; topicId: string };
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(10);
  const [diff, setDiff] = useState<string>("medium");
  const [type, setType] = useState<string>("multiple_choice");
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [generated, setGenerated] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [savingAll, setSavingAll] = useState(false);

  const gen = useServerFn(aiGenerateQuestions);
  const save = useServerFn(saveQuestion);

  const canGen = !!defaults.className && !!defaults.subject;

  const run = async () => {
    setBusy(true);
    try {
      const r = await gen({
        data: {
          className: defaults.className,
          subject: defaults.subject,
          topic: topic || undefined,
          count,
          types: [type],
          difficulty: diff as never,
          notes: notes || undefined,
        },
      });
      setGenerated(r.questions);
      toast.success(`Generated ${r.questions.length} questions`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const saveAll = async (publish: boolean) => {
    setSavingAll(true);
    let ok = 0;
    for (const q of generated) {
      try {
        await save({
          data: {
            type: q.type,
            difficulty: q.difficulty ?? (diff as never),
            bloom_level: q.bloom_level,
            stem: q.stem,
            correct_answer: q.correct_answer,
            explanation: q.explanation,
            marking_guide: q.marking_guide,
            marks: q.marks ?? 1,
            time_estimate_seconds: q.time_estimate_seconds,
            tags: q.tags,
            class_id: defaults.classId || undefined,
            subject_id: defaults.subjectId || undefined,
            topic_id: defaults.topicId || undefined,
            status: publish ? "approved" : "draft",
            options: q.options,
          } as never,
        });
        ok++;
      } catch (e) {
        console.error(e);
      }
    }
    setSavingAll(false);
    toast.success(`Saved ${ok}/${generated.length} questions`);
    setGenerated([]);
    setOpen(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-primary"><Sparkles className="h-4 w-4 mr-2" />AI Generate</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5" /> AI Question Generation</DialogTitle>
        </DialogHeader>
        {!canGen ? (
          <p className="text-sm text-muted-foreground">Pick a class and subject in the filters first.</p>
        ) : (
          <Tabs defaultValue="config">
            <TabsList>
              <TabsTrigger value="config">Configure</TabsTrigger>
              <TabsTrigger value="review" disabled={!generated.length}>Review ({generated.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="config" className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Class</Label>
                  <Input value={defaults.className} disabled />
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input value={defaults.subject} disabled />
                </div>
                <div>
                  <Label>Topic (optional override)</Label>
                  <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Fractions" />
                </div>
                <div>
                  <Label>Count</Label>
                  <Input type="number" min={1} max={40} value={count} onChange={(e) => setCount(Number(e.target.value))} />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{QTYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Difficulty</Label>
                  <Select value={diff} onValueChange={setDiff}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DIFFS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notes / instructions for the AI</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. UCE style, include Ugandan context, focus on respiration." />
              </div>
              <Button onClick={run} disabled={busy} className="w-full">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Generate
              </Button>
            </TabsContent>
            <TabsContent value="review" className="space-y-3">
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
                {generated.map((q, i) => (
                  <div key={i} className="border rounded-lg p-3">
                    <p className="text-sm font-medium">{i + 1}. {q.stem}</p>
                    {q.options?.length ? (
                      <ul className="mt-2 text-sm space-y-1">
                        {q.options.map((o: any, j: number) => (
                          <li key={j} className={`flex items-start gap-2 ${o.is_correct ? "text-primary font-medium" : ""}`}>
                            {o.is_correct ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <span className="w-4" />}
                            <span>{o.label}{o.value && o.value !== o.label ? `: ${o.value}` : ""}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground mt-1">Answer: {JSON.stringify(q.correct_answer)}</p>
                    )}
                    {q.explanation && <p className="mt-2 text-xs text-muted-foreground">💡 {q.explanation}</p>}
                    <div className="flex gap-1 mt-2">
                      <Badge variant="outline">{q.difficulty}</Badge>
                      <Badge variant="outline">{q.bloom_level}</Badge>
                      <Badge variant="outline">{q.marks ?? 1} mark</Badge>
                    </div>
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" disabled={savingAll} onClick={() => saveAll(false)}>
                  {savingAll && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save as drafts
                </Button>
                <Button disabled={savingAll} onClick={() => saveAll(true)}>
                  {savingAll && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save & approve
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ----------------- Bulk import dialog ----------------- */

function BulkImportDialog({
  defaults, onDone,
}: {
  defaults: { classId: string; subjectId: string; topicId: string };
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [json, setJson] = useState("");
  const [busy, setBusy] = useState(false);
  const importFn = useServerFn(bulkImportQuestions);

  const run = async () => {
    let rows: unknown;
    try { rows = JSON.parse(json); } catch { toast.error("Invalid JSON"); return; }
    if (!Array.isArray(rows)) { toast.error("Expected a JSON array of questions"); return; }
    setBusy(true);
    try {
      const r = await importFn({
        data: {
          subject_id: defaults.subjectId || undefined,
          class_id: defaults.classId || undefined,
          topic_id: defaults.topicId || undefined,
          rows: rows as never,
        },
      });
      toast.success(`Imported ${r.inserted}, duplicates ${r.duplicates}, failed ${r.failed}`);
      setOpen(false);
      setJson("");
      onDone();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline"><Plus className="h-4 w-4 mr-2" />Import</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileJson className="h-5 w-5" /> Bulk import (JSON)</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Paste a JSON array. Each item needs at least <code>type</code> and <code>stem</code>. Duplicates are detected by content hash.
        </p>
        <Textarea
          rows={14}
          value={json}
          onChange={(e) => setJson(e.target.value)}
          placeholder='[{"type":"multiple_choice","stem":"2+2=?","difficulty":"easy","marks":1,"options":[{"label":"3"},{"label":"4","is_correct":true},{"label":"5"},{"label":"22"}],"explanation":"Basic addition."}]'
          className="font-mono text-xs"
        />
        <DialogFooter>
          <Button onClick={run} disabled={busy || !json.trim()}>
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
